import {Checkout, CheckoutFactory} from "@connox/typescript-library";
import * as React from "react";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {getShopId} from "../../lib/getShopId";
import window from "../../lib/WindowExtended";
import {Payments} from "../../lib/payments/PaymentController";

interface ICheckoutContext {
  checkout: Checkout;
}

const checkoutContext = React.createContext<ICheckoutContext | null>(null);

export const useCheckoutContext = (): ICheckoutContext => {
  const context = React.useContext(checkoutContext);
  if (!context) {
    throw new Error("useCheckoutContext must be used in a component within a CheckoutContextProvider.");
  }

  return context;
};

interface ICheckoutContextProviderState {
  isLoading: boolean;
  checkoutContext: ICheckoutContext | null;
}

const CheckoutContextProvider: React.FC<{children?: React.ReactNode}> = (props) => {
  const [state, setState] = React.useState<ICheckoutContextProviderState>({checkoutContext: null, isLoading: true});
  const {cart} = useCartContext();
  const handleCheckoutEmitterEvents = () => {
    setState((prevState) => {
      if (!prevState) {
        // this might be an error case
        return {checkoutContext: null, isLoading: true};
      }

      return {checkoutContext: prevState.checkoutContext, isLoading: false};
    });
  };
  const checkoutRef = React.useRef<Checkout | null>(null);

  React.useEffect(() => {
    void CheckoutFactory.generateCheckout(getShopId(), cart).then((checkout) => {
      let wait = false;
      const setThings = () => {
        if (wait) {
          return;
        }
        setState({checkoutContext: {checkout}, isLoading: false});
        checkoutRef.current = checkout;
        checkout.emitter.on(handleCheckoutEmitterEvents);
      };

      if (checkout.paymentId) {
        const uspAuthenticationStatus = new URLSearchParams(window.location.search).get("AuthenticationStatus");
        if (
          (checkout.paymentId === Payments.AmazonPay && !window.orderReferenceId && !uspAuthenticationStatus) ||
          (checkout.paymentId === Payments.PaypalExpressBraintree && !window.pp_nonce)
        ) {
          wait = true;
          void checkout.put({paymentId: null}).then(() => {
            wait = false;
            setThings();
          });
        }
      }

      setThings();
    });

    return () => {
      if (!checkoutRef.current) {
        return;
      }
      checkoutRef.current.emitter.off(handleCheckoutEmitterEvents);
    };
  }, []);

  if (state.isLoading || !state.checkoutContext) {
    return null;
  }

  const {Provider} = checkoutContext;

  return <Provider value={state.checkoutContext}>{props.children}</Provider>;
};

export default CheckoutContextProvider;
